import React, { useContext } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import ClickableLink from '../Common/Clickable/ClickableLink'
import Clickable from '../Common/Clickable'
import { CaptionMedium } from './Typography/Caption'
import { BodyTextLarge, BodyTextMedium, BodyTextSmall } from './Typography/BodyText'
import { TypographyElement } from './Typography/Typography'
import { SVG_ICON_SIZE_CSS_VAR } from './SvgIcon'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { ProductPaletteContext } from 'contexts/ProductPaletteContext'

const LinkElement = styled(TypographyElement)`
  display: inline;
  font-family: ${props => props.theme.font.primary.family};
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s, outline 0.2s;

  &.block {
    display: block;
  }

  &.has-icon-start {
    > :first-child {
      margin-right: ${rem(8)};
    }
  }

  &.has-icon-end {
    > :last-child {
      margin-left: ${rem(8)};
    }
  }

  &.underline {
    text-decoration: underline currentcolor;
  }

  &.wrap-no-wrap {
    white-space: nowrap;
  }

  &.wrap-word-wrap {
    word-break: break-word;
  }

  &.wrap-letter-wrap {
    word-break: break-all;
  }

  &.wrap-truncate {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &.wrap-pre-wrap {
    white-space: pre-wrap;
  }

  color: var(--colour);

  &:disabled {
    color: var(--colour-disabled);
  }

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        color: var(--colour-hover);
        text-decoration: underline currentcolor;
      }
    }

    &:focus {
      ${interactiveFocusStyle}
    }

    .input-keyboard &:focus {
      text-decoration: underline currentcolor;
    }

    &:active, &.toggled {
      color: var(--colour-active)
    }
  }



  &.variant-default {
    --colour: ${props => props.theme.palette.brand.primary.normal};
    --colour-disabled: ${props => props.theme.palette.neutral.default.four};
    --colour-hover: ${props => props.theme.palette.brand.primary.dark};
    --colour-active: ${props => props.theme.palette.brand.primary.darkest};

    &.product-palette-tier-platinum {
      --colour: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
      --colour-disabled: ${props => props.theme.palette.neutral.default.four};
      --colour-hover: ${props => props.theme.palette.neutral.default.six};
      --colour-active: ${props => props.theme.palette.neutral.default.six};
    }
  }

  &.variant-ghost {
    --colour: ${props => props.theme.palette.neutral.default.eight};
    --colour-disabled: ${props => props.theme.palette.neutral.default.four};
    --colour-hover: ${props => props.theme.palette.neutral.default.eight};
    --colour-active: ${props => props.theme.palette.neutral.default.seven};
  }

  &.variant-black {
    --colour: ${props => props.theme.palette.neutral.default.one};
    --colour-disabled: ${props => props.theme.palette.neutral.default.four};
    --colour-hover: ${props => props.theme.palette.neutral.default.two};
    --colour-active: ${props => props.theme.palette.neutral.default.three};
  }

  &.variant-highlight {
    --colour: ${props => props.theme.palette.highlight.secondary.normalForeground};
    --colour-disabled: ${props => props.theme.palette.neutral.default.four};
    --colour-hover: ${props => props.theme.palette.highlight.secondary.normalForeground}
    --colour-active: ${props => props.theme.palette.highlight.secondary.normalActive};
  }

  &.variant-lux-plus {
    --colour: ${props => props.theme.palette.product.luxPlus.foreground};
    --colour-disabled: ${props => props.theme.palette.neutral.default.four};
    --colour-hover: ${props => props.theme.palette.product.luxPlus.hover};
    --colour-active: ${props => props.theme.palette.product.luxPlus.hover};
  }

  &.size-caption {
    ${CaptionMedium}
  }

  &.size-small {
    ${BodyTextSmall}
  }

  &.size-medium {
    ${BodyTextMedium}
  }

  &.size-large {
    ${BodyTextLarge}
    --link-icon-size: ${rem(20)};
  }

  &.weight-regular {
    font-weight: ${props => props.theme.font.primary.weight.regular};
  }

  &.weight-bold {
    font-weight: ${props => props.theme.font.primary.weight.superBold};
  }

  > img {
    height: var(${SVG_ICON_SIZE_CSS_VAR}, ${rem(16)});
    width: var(${SVG_ICON_SIZE_CSS_VAR}, ${rem(16)});
  }

  @media print {
    display: none;
  }
`

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * @default default
   */
  variant?: 'default' | 'ghost' | 'black' | 'highlight' | 'lux-plus';
  /**
   * @default bold
   */
  weight?: 'regular' | 'bold';
  size?: 'caption' | 'small' | 'medium' | 'large';
  wrap?: 'no-wrap' | 'word-wrap' | 'letter-wrap' | 'truncate' | 'pre-wrap';
  to?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /**
   * @default true
   */
  underline?: boolean;
  disabled?: boolean;
  /**
   * Sets the text link as a 'block' element
   * this will allow things like margin to space it
   * By default, this element is an inline element
   */
  block?: boolean;
}

const TextLink = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, Props>((props: Props, ref) => {
  const {
    variant = 'default',
    className,
    size,
    weight = 'bold',
    startIcon,
    endIcon,
    children,
    underline = true,
    wrap,
    block,
    ...rest
  } = props

  let component: any = null
  if (rest.href || rest.to) {
    component = ClickableLink
  } else if (rest.onClick) {
    component = Clickable
  } else {
    component = 'span'
  }

  const productPalette = useContext(ProductPaletteContext)

  return <LinkElement
    {...rest}
    as={component}
    ref={ref}
    className={cn(
      className,
      `variant-${variant}`,
      `weight-${weight}`,
      `product-palette-${productPalette}`,
      size ? `size-${size}` : undefined,
      wrap ? `wrap-${wrap}` : undefined,
      {
        block,
        underline,
        'has-icon-start': !!startIcon,
        'has-icon-end': !!endIcon,
      },
    )}
  >
    {startIcon}
    {children}
    {endIcon}
  </LinkElement>
})

TextLink.displayName = 'TextLink'

export default TextLink
